<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
          Invite Store User
        </h2>
        <button
          @click="closeModal"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <form @submit.prevent="submitForm" class="p-6 space-y-4">
        <!-- Username -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Username
          </label>
          <input
            v-model="form.username"
            type="text"
            placeholder="Enter username"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <!-- Invite Method Toggle -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Invite by
          </label>
          <div class="flex gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
            <button
              type="button"
              @click="form.inviteMethod = 'phone'"
              :class="[
                'flex-1 px-4 py-2 rounded-md font-medium transition',
                form.inviteMethod === 'phone'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              <svg class="inline-block w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Phone
            </button>
            <button
              type="button"
              @click="form.inviteMethod = 'email'"
              :class="[
                'flex-1 px-4 py-2 rounded-md font-medium transition',
                form.inviteMethod === 'email'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              <svg class="inline-block w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email
            </button>
          </div>
        </div>

        <!-- Phone Field -->
        <div v-if="form.inviteMethod === 'phone'">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Phone Number
          </label>
          <input
            v-model="form.phone"
            type="tel"
            placeholder="+250..."
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Must be in international format (e.g., +250787123456)
          </p>
        </div>

        <!-- Email Field -->
        <div v-else>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email Address
          </label>
          <input
            v-model="form.email"
            type="email"
            placeholder="user@example.com"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <!-- Permissions -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Permissions
          </label>
          <div class="space-y-4 max-h-64 overflow-y-auto">
            <div v-for="group in permissionGroups" :key="group.key">
              <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                {{ group.label }}
              </p>
              <label
                v-for="permission in group.options"
                :key="permission.value"
                class="flex items-start gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
              >
                <input
                  type="checkbox"
                  :checked="form.permissions.includes(permission.value)"
                  @change="togglePermission(permission.value, $event.target.checked)"
                  class="w-4 h-4 mt-0.5 text-blue-500 rounded border-gray-300 dark:border-gray-600"
                />
                <span>
                  <strong class="block text-sm text-gray-700 dark:text-gray-300">
                    {{ permission.label }}
                  </strong>
                  <small class="block text-xs text-gray-500 dark:text-gray-400">
                    {{ permission.description }}
                  </small>
                </span>
              </label>
            </div>
          </div>
          <p v-if="form.permissions.length === 0" class="text-xs text-amber-600 dark:text-amber-400 mt-2">
            ⚠️ Select at least one permission
          </p>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p class="text-sm text-red-700 dark:text-red-300">{{ errorMessage }}</p>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            @click="closeModal"
            class="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isLoading"
            class="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition flex items-center justify-center gap-2"
          >
            <svg
              v-if="isLoading"
              class="w-4 h-4 animate-spin"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {{ isLoading ? 'Inviting...' : 'Send Invite' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import {
  closePermissionDependencies,
  groupPermissionCatalog,
  updatePermissionSelection,
} from '@/helper/storePermissions';

export default {
  name: 'InviteUserModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    storeId: {
      type: [String, Number],
      required: true,
    },
    availablePermissions: {
      type: Array,
      default: () => [
        'CAN_MANAGE_USERS',
        'CAN_MANAGE_INVENTORY',
        'CAN_MANAGE_REPORTS',
        'CAN_VIEW_REPORTS',
      ],
    },
  },
  emits: ['close', 'invite-sent'],
  setup(props, { emit }) {
    const form = ref({
      username: '',
      phone: '',
      email: '',
      inviteMethod: 'phone',
      permissions: [],
    });

    const errorMessage = ref('');
    const isLoading = ref(false);
    const permissionGroups = computed(() =>
      groupPermissionCatalog(props.availablePermissions),
    );

    const togglePermission = (permission, selected) => {
      form.value.permissions = updatePermissionSelection(
        form.value.permissions,
        permission,
        selected,
        props.availablePermissions,
      );
    };

    const closeModal = () => {
      form.value = {
        username: '',
        phone: '',
        email: '',
        inviteMethod: 'phone',
        permissions: [],
      };
      errorMessage.value = '';
      emit('close');
    };

    const submitForm = async () => {
      errorMessage.value = '';

      // Validation
      if (!form.value.username.trim()) {
        errorMessage.value = 'Username is required';
        return;
      }

      if (form.value.permissions.length === 0) {
        errorMessage.value = 'Select at least one permission';
        return;
      }

      if (form.value.inviteMethod === 'phone' && !form.value.phone.trim()) {
        errorMessage.value = 'Phone number is required';
        return;
      }

      if (form.value.inviteMethod === 'email' && !form.value.email.trim()) {
        errorMessage.value = 'Email address is required';
        return;
      }

      isLoading.value = true;

      try {
        const payload = {
          store_id: props.storeId,
          username: form.value.username,
          permissions: closePermissionDependencies(form.value.permissions),
        };

        if (form.value.inviteMethod === 'phone') {
          payload.phone_number = form.value.phone;
        } else {
          payload.email = form.value.email;
        }

        // TODO: Call API to invite user
        // const response = await inviteStoreUser(payload);

        emit('invite-sent', {
          method: form.value.inviteMethod,
          payload,
        });

        closeModal();
      } catch (error) {
        errorMessage.value = error.message || 'Failed to send invitation';
      } finally {
        isLoading.value = false;
      }
    };

    return {
      form,
      errorMessage,
      isLoading,
      permissionGroups,
      closeModal,
      submitForm,
      togglePermission,
    };
  },
};
</script>
